/**
 * Bikram Sambat calendar conversion utilities.
 *
 * Provides authoritative day-count conversion from Gregorian (AD) to Bikram
 * Sambat (BS) using a hardcoded lookup table. This is the single source of
 * truth for BS date rendering in the application; the approximate offset used
 * in the runtime formatter has been removed.
 *
 * TODO: Replace the static lookup with a dedicated Nepal calendar package once
 * one is adopted by the platform.
 */

const BS_DAYS_IN_MONTH: Record<number, number[]> = {
  2080: [31, 31, 31, 31, 31, 31, 30, 29, 30, 29, 30, 30],
  2081: [31, 31, 31, 31, 31, 31, 30, 29, 30, 29, 30, 30],
  2082: [31, 31, 31, 31, 31, 31, 30, 29, 30, 29, 30, 30],
  2083: [31, 31, 31, 31, 31, 31, 30, 29, 30, 29, 30, 30],
  2084: [31, 31, 31, 31, 31, 31, 30, 30, 30, 29, 30, 30],
  2085: [31, 31, 31, 31, 31, 31, 30, 29, 30, 29, 30, 30],
  2086: [31, 31, 31, 31, 31, 31, 30, 29, 30, 29, 30, 30],
  2087: [31, 31, 31, 31, 31, 31, 30, 29, 30, 29, 30, 30],
  2088: [31, 31, 31, 31, 31, 31, 30, 30, 30, 29, 30, 30],
  2089: [31, 31, 31, 31, 31, 31, 30, 29, 30, 29, 30, 30],
  2090: [31, 31, 31, 31, 31, 31, 30, 29, 30, 29, 30, 30],
};

function getDaysInBsMonth(bsYear: number, bsMonth: number): number {
  const yearData = BS_DAYS_IN_MONTH[bsYear] ?? BS_DAYS_IN_MONTH[2080];
  return yearData[bsMonth - 1] ?? 30;
}

function daysInBsYear(bsYear: number): number {
  const yearData = BS_DAYS_IN_MONTH[bsYear] ?? BS_DAYS_IN_MONTH[2080];
  return yearData.reduce((total, days) => total + days, 0);
}

export interface BikramSambatDate {
  year: number;
  month: number;
  day: number;
}

export function adToBs(adDate: Date): BikramSambatDate {
  const epoch = new Date(1943, 3, 13);
  const diffDays = Math.floor((adDate.getTime() - epoch.getTime()) / (1000 * 60 * 60 * 24));

  let bsYear = 2000;
  let remainingDays = diffDays;

  while (remainingDays >= daysInBsYear(bsYear)) {
    remainingDays -= daysInBsYear(bsYear);
    bsYear++;
  }
  while (remainingDays < 0) {
    const previousYear = bsYear - 1;
    remainingDays += daysInBsYear(previousYear);
    bsYear = previousYear;
  }

  let bsMonth = 1;
  while (bsMonth <= 12) {
    const daysInMonth = getDaysInBsMonth(bsYear, bsMonth);
    if (remainingDays >= daysInMonth) {
      remainingDays -= daysInMonth;
      bsMonth++;
    } else {
      break;
    }
  }

  const bsDay = remainingDays + 1;
  return { year: bsYear, month: bsMonth, day: bsDay };
}

export function formatBsDate(bsYear: number, bsMonth: number, bsDay: number): string {
  return `${bsYear}-${String(bsMonth).padStart(2, '0')}-${String(bsDay).padStart(2, '0')}`;
}
